import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import cx from "classnames";
import _ from "lodash";
import dateformat from "dateformat";
import {
  ReactFlow,
  Background,
  useNodesState,
  applyNodeChanges,
  useEdgesState,
  useStoreApi,
  useReactFlow,
} from "@xyflow/react";
import dagre from "@dagrejs/dagre";
import shortid from "shortid";

import { AssetNode, ConditionNode, SelectorNode } from "../nodes";
import DynamicManager from "../DynamicManager";
import ManualManager from "../ManualManager";
import {
  calculateNodePositions,
  constructCommands,
  getCenterSelectorPosition,
  getParentNode,
  getRecursiveChildEdges,
  getRecursiveChildNodes,
  getSourceHandle,
} from "./helper";
import { invalidateFilterSortInContentManager } from "actions/content";
import "@xyflow/react/dist/style.css";

import styles from "./chartEditor.module.scss";

const ChartEditor = () => {
  const connectionLineStyle = { stroke: "#FFC600" };
  const snapGrid = [20, 20];
  const store = useStoreApi();
  const layoutAppliedRef = useRef(false);
  const reactFlowInstanceRef = useRef(null);
  const nodeTypes = {
    selectorNode: SelectorNode,
    assetNode: AssetNode,
    conditionNode: ConditionNode,
  };
  const { fitView, setViewport, getViewport } = useReactFlow();
  const defaultViewport = { x: 0, y: 0, zoom: 1 };
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));

  let { journeyId } = useParams();
  const journeySteps =
    useSelector((state) => _.get(state, `journey.${journeyId}.steps`, [])) ||
    [];
  const journeyConnectors =
    useSelector((state) =>
      _.get(state, `journey.${journeyId}.connectors`, [])
    ) || [];
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgeChanges] = useEdgesState([]);
  const [showContentManager, setShowContentManager] = useState({
    selectedNode: null,
    show: false,
    type: "MANUAL",
  });

  const nodeDimensions = {
    assetNode: { width: 224, height: 166 },
    conditionNode: { width: 150, height: 150 },
    selectorNode: { width: 44, height: 44 },
  };

  const getLayoutedElements = (nodes, edges, direction = "TB") => {
    const dagreGraph = new dagre.graphlib.Graph().setDefaultEdgeLabel(
      () => ({})
    );
    let nodeUpdatedPositions={};

    dagreGraph.setGraph({
      rankdir: direction,
      nodesep: 100, // Increase the space between nodes
      edgesep: 10, // Space between edges
      ranksep: 100,
    });

    nodes.forEach((node) => {
      const { width, height } = nodeDimensions[node.type];
      dagreGraph.setNode(node.id, { width, height });
    });

    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    // Offset for initial positioning
    const xOffset = 300;
    const yOffset = 100;

    const newNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      const { width, height } = nodeDimensions[node.type];
      const parentNode = nodes.find((n) => n.id === node.parentNode);

      let position = {
        x: nodeWithPosition.x - width / 2 + xOffset,
        y: nodeWithPosition.y - height / 2 + yOffset,
      };

      nodeUpdatedPositions[node.id] = {x: nodeWithPosition.x, y: nodeWithPosition.y}

      // Adjust positions for "Yes" and "No" selector nodes of a condition node
      if (parentNode) {
        let parentNodePosition = dagreGraph.node(parentNode.id);

        switch (parentNode.type) {
          case "conditionNode":
            const childConnectors = edges.filter(
              (connector) => connector.source === parentNode.id
            );
            const nodeConnectorData = _.find(childConnectors, (connector) => {
              return connector.target == node.id;
            });

            // if(nodeUpdatedPositions[parentNode.id]){
            //   parentNodePosition = nodeUpdatedPositions[parentNode.id]
            // }
            // Manual positioning for "Yes" and "No" nodes relative to the condition node

            
            switch (nodeConnectorData.sourceHandle) {
              case "yes_source":
                let noSourcePosition = _.find(childConnectors, (connector) => {
                  return connector.sourceHandle == "no_source";
                });
                noSourcePosition = dagreGraph.node(noSourcePosition.target);
                const yesParentDiff = nodeUpdatedPositions[parentNode.id].x - nodeWithPosition.x
                const yesNodeDiff = parentNodePosition.x - nodeWithPosition.x; 

                if((nodeWithPosition.x < noSourcePosition.x) || yesParentDiff !== yesNodeDiff) {

                  position.x = (nodeWithPosition.x < noSourcePosition.x) ? (nodeUpdatedPositions[parentNode.id].x + yesNodeDiff) - width / 2 + xOffset : (nodeUpdatedPositions[parentNode.id].x - yesNodeDiff) - width / 2 + xOffset
                  nodeUpdatedPositions[node.id] = {x:( (nodeWithPosition.x < noSourcePosition.x) ? nodeUpdatedPositions[parentNode.id].x + yesNodeDiff: (nodeUpdatedPositions[parentNode.id].x - yesNodeDiff)), y: nodeWithPosition.y}
                }
                break;

              case "no_source":
                let yesSourcePosition = _.find(childConnectors, (connector) => {
                  return connector.sourceHandle == "yes_source";
                });
                yesSourcePosition = dagreGraph.node(yesSourcePosition.target);
                const noParentDiff =  nodeWithPosition.x - nodeUpdatedPositions[parentNode.id].x
                const noNodeDiff = nodeWithPosition.x - parentNodePosition.x; 

                if((nodeWithPosition.x > yesSourcePosition.x) || noNodeDiff !== noParentDiff ) {
                  
                  position.x = nodeWithPosition.x > yesSourcePosition.x ? ( nodeUpdatedPositions[parentNode.id].x - noNodeDiff) - width / 2 + xOffset: ( nodeUpdatedPositions[parentNode.id].x + noNodeDiff) - width / 2 + xOffset
                  nodeUpdatedPositions[node.id] = {x: nodeWithPosition.x > yesSourcePosition.x ? (nodeUpdatedPositions[parentNode.id].x - noNodeDiff): ( nodeUpdatedPositions[parentNode.id].x + noNodeDiff) , y: nodeWithPosition.y}
                }
                break;
            }
          break;

          case "assetNode":
            const updatedParentPosition = nodeUpdatedPositions[parentNode.id];

            if (nodeWithPosition.x !== updatedParentPosition.x) {
              position.x = updatedParentPosition.x - width / 2 + xOffset;
              nodeUpdatedPositions[node.id] = {x: updatedParentPosition.x , y: nodeWithPosition.y}
            }

          break;
        }
      }

      return {
        ...node,
        position,
      };
    });

    return { nodes: newNodes, edges };
  };

  const onInit = (reactFlowInstance) => {
    reactFlowInstanceRef.current = reactFlowInstance;

    setTimeout(() => {
      reactFlowInstance.fitView({
        padding: 0.2, // Adjust for more precise fitting
        includeHiddenNodes: true, // Ensures all nodes are included
      });
      const currentViewport = getViewport(); // Get current x, y, and zoom values
      setViewport(
        { x: currentViewport.x, y: 0, zoom: currentViewport.zoom }, // Update only y
        { duration: 300 } // Smooth transition
      );
    }, 0);


  };

  useEffect(() => {
    if (_.size(journeySteps) > 0 && _.size(journeyConnectors) > 0) {
      const constructStep = _.map(journeySteps, (step) => {
        const parentData = getParentNode(
          journeyConnectors,
          journeySteps,
          step.id
        );

        switch (step.type) {
          case "CONTENT":
            if (step.start) {
              return {
                id: step.id,
                type: "assetNode",
                data: {
                  handleToggleContentManager: handleToggleContentManager,
                  handleSetValue: handleSetValue,
                  value: step.mapping,
                },
                draggable: false,
                position: { x: 0, y: 0 },
                sourcePosition: "bottom",
              };
            }

            return {
              id: step.id,
              type: "assetNode",
              data: {
                handleToggleContentManager: handleToggleContentManager,
                handleSetValue: handleSetValue,
                value: step.mapping,
              },
              draggable: false,
              parentNode: _.get(parentData, "id"),
              position: { x: 0, y: 0 },
              targetPosition: "top",
              sourcePosition: "bottom",
            };

          case "CONDITION":
            return {
              id: step.id,
              type: "conditionNode",
              data: {
                handleToggleContentManager: handleToggleContentManager,
                handleSetValue: handleSetValue,
                value: step.mapping,
              },
              draggable: false,
              parentNode: _.get(parentData, "id"),
              position: { x: 0, y: 0 },
              targetPosition: "top",
            };
        }
      });

      setNodes(constructStep);

      const constructConnectors = _.map(journeyConnectors, (connector) => {
        const { start, end, filters } = connector;
        const parentData = _.find(journeySteps, (step) => {
          return step.id === start;
        });

        if (!end && parentData) {
          const targetId = shortid();
          createSelectorNode(targetId, parentData, connector);

          if (parentData.type === "CONDITION") {
            const { operator } = _.head(filters);

            switch (operator) {
              case "IS_GREATER_THAN":
                return {
                  id: `${start}-${targetId}`,
                  source: start,
                  data: {
                    commands: filters,
                  },
                  target: targetId,
                  label: "NO",
                  sourceHandle: "no_source",
                  style: { stroke: "#FFC600", width: "2px" },
                  type: "smoothstep",
                };

              case "IS_LESS_THAN":
                return {
                  id: `${start}-${targetId}`,
                  source: start,
                  data: {
                    commands: filters,
                  },
                  label: "YES",
                  sourceHandle: "yes_source",
                  target: targetId,
                  style: { stroke: "#FFC600", width: "2px" },
                  type: "smoothstep",
                };
            }
          }

          return {
            id: `${start}-${targetId}`,
            source: start,
            data: {
              commands: filters,
            },
            target: targetId,
            style: { stroke: "#FFC600", width: "2px" },
            type: "smoothstep",
          };
        }

        if (filters && start) {
          const { operator } = _.head(filters);

          switch (operator) {
            case "IS":
              return {
                id: `${start}-${end}`,
                source: start,
                data: {
                  commands: filters,
                },
                target: end,
                style: { stroke: "#FFC600", width: "2px" },
                type: "smoothstep",
              };

            case "IS_GREATER_THAN":
              return {
                id: `${start}-${end}`,
                source: start,
                data: {
                  commands: filters,
                },
                target: end,
                label: "NO",
                sourceHandle: "no_source",
                style: { stroke: "#FFC600", width: "2px" },
                type: "smoothstep",
              };

            case "IS_LESS_THAN":
              return {
                id: `${start}-${end}`,
                source: start,
                data: {
                  commands: filters,
                },
                label: "YES",
                sourceHandle: "yes_source",
                target: end,
                style: { stroke: "#FFC600", width: "2px" },
                type: "smoothstep",
              };
          }
        }

        return {
          id: `${start}-${end}`,
          source: start,
          data: {
            commands: filters,
          },
          target: end,
          style: { stroke: "#FFC600", width: "2px" },
          type: "smoothstep",
        };
      });

      setEdges(constructConnectors);
      return;
    }

    createInitialStep();
  }, []);

  const createSelectorNode = (id, parentData, connector) => {

    if (!parentData) {
      return;
    }
    
    const { type } = parentData;

    switch (type) {
      case "CONTENT":
        setNodes((nodes) => {
          return [
            ...nodes,
            {
              id: id,
              type: "selectorNode",
              data: {
                handleAddNode: handleAddNode,
                disabled: false,
              },
              parentNode: `${parentData.id}`,
              draggable: false,
              targetPosition: "top",
              position: { x: 0, y: 0 },
            },
          ];
        });
        break;

      case "CONDITION":
        const source = getSourceHandle(connector);
        setNodes((nodes) => {
          return [
            ...nodes,
            {
              id: id,
              type: "selectorNode",
              data: {
                handleAddNode: handleAddNode,
                disabled: true,
              },
              parentNode: `${parentData.id}`,
              label: source ? "yes" : "No",
              draggable: false,
              targetPosition: "top",
              position: { x: 0, y: 0 },
            },
          ];
        });
        break;
    }
  };

  const handleAddNode = (selectorId, type) => {
    // Replace the selector with an AssetCard node
    switch (type) {
      case "assetNode":
        const assetSelectortNodeId = shortid.generate();

        setNodes((prevNodes) => {
          const updatedNodes = prevNodes.map((node) => {
            if (node.id === selectorId) {
              // Replace the current selector with an AssetCard
              return {
                ...node,
                type: type,
                data: {
                  value: null,
                  handleSetValue: handleSetValue,
                  handleToggleContentManager: handleToggleContentManager,
                },
                targetPosition: "top",
                sourcePosition: "bottom",
              };
            }
            return node;
          });

          return [
            ...updatedNodes,
            {
              id: assetSelectortNodeId,
              type: "selectorNode",
              data: {
                handleAddNode: handleAddNode,
                disabled: true,
              },
              parentNode: `${selectorId}`,
              draggable: false,
              targetPosition: "top",
              position: { x: 0, y: 0 },
            },
          ];
        });

        // Update the edges: connect the new AssetCard to the new Selector
        setEdges((edges) => {
          return [
            ...edges,
            {
              id: `${selectorId}-${assetSelectortNodeId}`,
              source: selectorId,
              target: assetSelectortNodeId,
              style: { stroke: "#FFC600", width: "2px" },
              type: "smoothstep",
            },
          ];
        });
        break;

      case "conditionNode":
        const condSelectortYesId = shortid.generate();
        const condSelectortNoId = shortid.generate();

        setNodes((prevNodes) => {
          const updatedNodes = prevNodes.map((node) => {
            if (node.id === selectorId) {
              // Replace the current selector with an AssetCard
              return {
                ...node,
                type: type,
                data: {
                  value: null,
                  handleSetValue: handleSetValue,
                  handleAddNode: handleAddNode,
                },
                targetPosition: "top",
              };
            }
            return node;
          });

          // Add the new Selector node
          return [
            ...updatedNodes,
            {
              id: condSelectortYesId,
              type: "selectorNode",
              data: {
                handleAddNode: handleAddNode,
                disabled: true,
                source: "conditionNode",
              },
              parentNode: `${selectorId}`,
              label: "YES",
              draggable: false,
              targetPosition: "top",
              position: { x: 0, y: 0 },
            },
            {
              id: condSelectortNoId,
              type: "selectorNode",
              data: {
                handleAddNode: handleAddNode,
                disabled: true,
                source: "conditionNode",
              },
              parentNode: `${selectorId}`,
              label: "No",
              draggable: false,
              targetPosition: "top",
              position: { x: 0, y: 0 },
            },
          ];
        });

        // Update the edges: connect the new AssetCard to the new Selector
        setEdges((edges) => {
          return [
            ...edges,
            {
              id: `${selectorId}-${condSelectortYesId}`,
              source: selectorId,
              target: condSelectortYesId,
              label: "YES",
              sourceHandle: "yes_source",
              style: { stroke: "#FFC600", width: "2px" },
              type: "smoothstep",
            },
            {
              id: `${selectorId}-${condSelectortNoId}`,
              source: selectorId,
              target: condSelectortNoId,
              label: "NO",
              sourceHandle: "no_source",
              style: { stroke: "#FFC600", width: "2px" },
              type: "smoothstep",
            },
          ];
        });
        break;

      default:
        break;
    }
  };

  // create trigger nodes
  const createInitialStep = () => {
    const triggerUid = shortid.generate();
    const selectorUid = shortid.generate();

    setNodes([
      {
        id: triggerUid,
        type: "assetNode",
        data: {
          handleToggleContentManager: handleToggleContentManager,
          handleSetValue: handleSetValue,
          value: null,
        },
        position: { x: 300, y: 50 },
        draggable: false,
        sourcePosition: "bottom",
      },
      {
        id: selectorUid,
        type: "selectorNode",
        draggable: false,
        data: {
          handleAddNode: handleAddNode,
          disabled: true,
        },
        parentNode: triggerUid,
        targetPosition: "top",
        position: { x: 390, y: 300 },
      },
    ]);

    setEdges([
      {
        id: `${triggerUid}-${selectorUid}`,
        source: triggerUid,
        target: selectorUid,
        style: { stroke: "#FFC600", width: "2px" },
        type: "smoothstep",
      },
    ]);
  };

  const handleSetValue = (id, value) => {
    if (id) {
      setNodes((prevNodes) => {
        const updatedNodes = prevNodes.map((node) => {
          if (node.id === id) {
            const getvalue = () => {
              if (node.type == "assetNode") {
                return value.mapping;
              }
              return value.filters[0];
            };
            // Replace the current selector with an AssetCard
            return {
              ...node,
              data: {
                ...node.data,
                value: getvalue(),
                description: value?.description
              },
            };
          }

          if (node.parentNode == id) {
            const parentNode = _.find(prevNodes, function (node) {
              return node.id === id;
            });

            if (parentNode.type === "conditionNode") {
              return {
                ...node,
                data: {
                  ...node.data,
                  source: "conditionNode",
                  disabled: false,
                },
              };
            }

            return {
              ...node,
              data: {
                ...node.data,
                disabled: false,
              },
            };
          }
          return node;
        });

        // Add the new Selector node
        return updatedNodes;
      });

      setEdges((prevEdges) => {
        const updatedEdges = prevEdges.map((edge) => {
          const conditional = edge.sourceHandle;
          if (edge.source === id) {
            // Replace the current selector with an AssetCard
            return {
              ...edge,
              data: {
                commands: constructCommands(value.filters, conditional),
              },
            };
          }

          return edge;
        });

        // Add the new Selector node
        return updatedEdges;
      });
    }
  };

  const handleToggleContentManager = (value) => {
    setShowContentManager(value);
  };

  const onBeforeDelete = ({ nodes }) => {
    const deletedNode = nodes[0];
    
    if (deletedNode?.parentNode) {
      requestAnimationFrame(() => {
        setNodes((nds) => {
          const childrens = getRecursiveChildNodes(nds, deletedNode.id);
          const parentDetails = _.find(nds, (data) => data.id === deletedNode.parentNode);
          
          const updatedNodes = nds.map((node) => {
            if (node.id === deletedNode.id) {
              const measuredWidth = node?.measured?.width || 0;
              const positionX = (node?.position?.x + (measuredWidth/2)) - 25;
              
              return {
                id: node.id,
                type: "selectorNode",
                draggable: false,
                data: {
                  handleAddNode: handleAddNode,
                  value: null,
                  source: parentDetails?.type,
                  disabled: (!_.get(parentDetails, "data.value") && !_.get(parentDetails, "data.description")),
                },
                parentNode: `${node.parentNode}`,
                targetPosition: "top",
                position: {x: positionX, y: node?.position?.y || 0},
              };
            }
            return node;
          });          
          return updatedNodes.filter((data) => !childrens.includes(data.id));
        });
        
        setEdges((edges) => {
          const childEdges = getRecursiveChildEdges(edges, deletedNode.id);
          return edges.filter((edge) => !childEdges.includes(edge));
        });
      });
      
      return;
    }
    
    createInitialStep();
  };

  const handleNodesChange = useCallback(
    (changes) => {
      onNodesChange(changes);

      if (!layoutAppliedRef.current) {
        const { nodes: layoutedNodes, edges: layoutedEdges } =
          getLayoutedElements(nodes, edges);
        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
        layoutAppliedRef.current = true; // Set ref to avoid repeat layout
      } else {
        layoutAppliedRef.current = false; // Reset for future changes
      }
    },
    [nodes, edges, setNodes, setEdges]
  );

  return (
    <>
      <ReactFlow
        nodes={nodes}
        className={styles.reactFlowChart}
        edges={edges}
        snapGrid={snapGrid}
        onBeforeDelete={onBeforeDelete}
        onNodesChange={handleNodesChange}
        connectionLineStyle={connectionLineStyle}
        nodeTypes={nodeTypes}
        defaultViewport={defaultViewport}
        fitViewOptions={{ padding: 0.1 }}
        onInit={onInit}
      >
        <Background bgColor="#EEEEEE" gap={20} variant="dots" />
      </ReactFlow>
      <NodeAssetManager
        showContentManager={showContentManager}
        handleToggleContentManager={handleToggleContentManager}
      />
    </>
  );
};

const NodeAssetManager = ({
  showContentManager,
  handleToggleContentManager,
}) => {
  const { show, type, selectedNode } = showContentManager;
  let { journeyId } = useParams();
  const dispatch = useDispatch();

  const onClose = () => {
    handleToggleContentManager({
      ...showContentManager,
      selectorNode: null,
      show: false,
    });

    if (type === "MANUAL") {
      dispatch(invalidateFilterSortInContentManager());
    }
  };

  return (
    <>
      {type === "MANUAL" && (
        <ManualManager
          show={show}
          journeyId={journeyId}
          selectedNode={selectedNode}
          onClose={onClose}
        />
      )}
      {type === "DYNAMIC" && (
        <DynamicManager
          show={show}
          journeyId={journeyId}
          selectedNode={selectedNode}
          onClose={onClose}
        />
      )}
    </>
  );
};

export default ChartEditor;
